import React from 'react';

import { Box } from '@mui/material';

import { useGetTableEntries } from '../../../../../../shared/hooks/use-get-table-entries/use-get-table-entries.hook';
import { useGetTableResults } from '../../../../../../shared/hooks/use-get-table-results/use-get-table-results.hook';
import { useResetPageNumberAfterFiltering } from '../../../../../../shared/hooks/use-reset-page-number-after-filtering/use-reset-page-number-after-filtering.hook';
import { onChangeTableRowsPerPage } from '../../../../../../shared/utils/on-change-table-rows-per-page/on-change-table-rows-per-page.util';
import { onTablePageChange } from '../../../../../../shared/utils/on-table-page-change/on-table-page-change.util';
import { useAppDispatch } from '../../../../../../store/hooks';
import NoResults from '../../../../../shared/components/no-results/NoResults';
import SDCTable from '../../../../../shared/components/ui/Table/components/table/SDCTable';
import { getPaginationConfig } from '../../../../../shared/components/ui/Table/utils/get-pagination-config.util';
import { CampaignFilters } from '../../../../models/campaign-filters.model';
import { CampaignOverviewModel } from '../../../../models/campaign-overview.model';
import {
  addPagination,
  resetPagination,
} from '../../../../store/slices/campaigns.slice';
import { CampaignsHeaderAndPaginationFilters } from '../../../../store/slices/models/campaigns-header-and-pagination-filters.model';
import { getCampaignsTotalCount } from '../../../../thunks/get-campaigns-total-count.thunk';
import { getCampaigns } from '../../../../thunks/get-campaigns.thunk';
import { useGetCampaignsTableSelectors } from './hooks/use-get-campaigns-table-selectors/use-get-campaigns-table-selectors.hook';
import { campaignsOverviewTableColumnsDef } from './utils/campaigns-overview-table-columns-def.util';

export const CampaignsTable = (): JSX.Element => {
  const {
    campaigns: tableRows,
    isLoading,
    activeMandantId,
    campaignFilters,
    campaignHeaderFilters,
    count,
    page,
    rowsPerPage,
  } = useGetCampaignsTableSelectors();

  const dispatch = useAppDispatch();
  const nameFilter = campaignFilters.header.name;

  useGetTableEntries<CampaignFilters>(
    campaignHeaderFilters,
    getCampaignsTotalCount,
    activeMandantId
  );

  useGetTableResults<
    CampaignsHeaderAndPaginationFilters,
    CampaignOverviewModel[]
  >(campaignFilters, getCampaigns, nameFilter, activeMandantId);

  useResetPageNumberAfterFiltering(count, page, rowsPerPage, resetPagination);

  return (
    <Box data-testid="campaign-overview-table">
      {tableRows?.length > 0 && (
        <SDCTable
          sx={{
            td: {
              '&:not(:first-of-type)': {
                verticalAlign: 'top',
                lineHeight: 3,
              },
              wordWrap: 'break-word',
              overflow: 'hidden',
            },
          }}
          ariaLabel="campaigns-overview-table"
          bulkActions={false}
          checkboxSelection={false}
          columns={campaignsOverviewTableColumnsDef}
          rows={tableRows}
          stickyHeader={true}
          paginationConfig={getPaginationConfig(
            count,
            rowsPerPage,
            page,
            onTablePageChange(dispatch, rowsPerPage, addPagination),
            onChangeTableRowsPerPage(dispatch, addPagination)
          )}
        />
      )}
      {tableRows?.length === 0 && !isLoading && <NoResults />}
    </Box>
  );
};
